import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-apollo';

import { truncateString } from '../../../util/text';
import ContributorBusinessCardModal from '../ContributorBusinessCard';
import Button from '../Button';
import { MeQuery } from '../../../graphql/queries/account';
import useAuthenticated from '../../../hooks/useAuthenticated';

const propTypes = {
  displayName: PropTypes.string,
  displayUsername: PropTypes.string,
  displayPhotoSrc: PropTypes.string,
  onClick: PropTypes.func,
  link: PropTypes.string,
  prependText: PropTypes.string,
  className: PropTypes.string,
  href: PropTypes.string,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  characterWidthLimit: PropTypes.number,
  accountId: PropTypes.string,
  photoAttributionPageEnabled: PropTypes.bool,
};

// If displayUsername is set it is shown as the main name and the username - if set - comes in brackets second.
const ContributorProfileBadge = ({
  // Overly high default to ensure no unnecessary truncation.
  characterWidthLimit = 200,
  displayName,
  displayUsername,
  displayPhotoSrc,
  prependText,
  className,
  href,
  to,
  accountId,
  photoAttributionPageEnabled,
  ...props
}) => {
  const authenticated = useAuthenticated();
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const [showBusinessCard, setShowBusinessCard] = useState(false);
  const classes = classNames({
    'contributor-link-container': true,
    [className]: className,
  });
  const halfWidthLimit = Math.floor(characterWidthLimit / 2);
  const isViewingOwnedContent = accountId === user?.id;

  // eslint-disable-next-line react/prop-types
  const ConditionalLink = ({ children }) => {
    const linkClass = 'contributor-profile-badge';

    if (photoAttributionPageEnabled && accountId) {
      return (
        <Button
          btnStyle="reset"
          className={linkClass}
          onClick={() => setShowBusinessCard(true)}
        >
          {children}
        </Button>
      );
    }

    if (to) {
      return (
        <Link to={to} className={linkClass} {...props}>
          {children}
        </Link>
      );
    }

    if (href) {
      return (
        <a href={href} className={linkClass} {...props}>
          {children}
        </a>
      );
    }

    return (
      <span className={linkClass} {...props}>
        {children}
      </span>
    );
  };

  const displayText = (() => {
    if (isViewingOwnedContent) {
      return <span>You</span>;
    }

    if (displayUsername && displayName) {
      return (
        <Fragment>
          <span className="display-name">
            {truncateString(displayName, halfWidthLimit)}
          </span>{' '}
          <span className="display-username">
            (@{truncateString(displayUsername, halfWidthLimit)})
          </span>
        </Fragment>
      );
    }
    if (displayUsername) {
      return truncateString(displayUsername, characterWidthLimit);
    }
    return displayName;
  })();

  return (
    <div className={classes}>
      {prependText && <div className="contributor-label">{prependText}</div>}
      <ConditionalLink
        className="contributor-profile-badge"
        href={href}
        to={to}
      >
        {displayPhotoSrc && (
          <span
            className="contributor-selfie"
            style={{ backgroundImage: `url("${displayPhotoSrc}")` }}
          />
        )}
        {displayText && (
          <span className="contributor-title">{displayText}</span>
        )}
        {photoAttributionPageEnabled && !isViewingOwnedContent && (
          <div className="contributor-get-in-touch">Get in touch</div>
        )}
      </ConditionalLink>
      {showBusinessCard && (
        <ContributorBusinessCardModal
          forceOpen
          accountId={accountId}
          onClose={() => setShowBusinessCard(false)}
        />
      )}
    </div>
  );
};

ContributorProfileBadge.propTypes = propTypes;

export default ContributorProfileBadge;
